import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../../../app/store";
import { createAppAsyncThunk } from "../../../../app/thunkWithTypes";
import Position from "../../../../api/models/portfolioPosition";
import PortfolioComposite from "../../../../api/models/portfolioComposite";
import { deletePosition, deleteComposite, getUser } from "../../../../api/client";
import { instanceToPlain } from "class-transformer";

interface CommandBarState {
  removePositionStatus: 'idle' | 'pending' | 'succeeded' | 'failed'
  removeCompositeStatus: 'idle' | 'pending' | 'succeeded' | 'failed'
}

const initialState: CommandBarState = {
  removePositionStatus: 'idle',
  removeCompositeStatus: 'idle'
}

export const removeComposite = createAppAsyncThunk('commandBar/removeComposite', async (data: { accessToken: string, composite: PortfolioComposite }) => {
  const { accessToken, composite } = data;
  const user = await getUser(accessToken);
  if (user && composite) {
    if (await deleteComposite(accessToken, user.id, composite)) {
      return instanceToPlain(composite);
    };
  } else {
    return undefined;
  }
}, {
  condition(arg, thunkAPi) {
    const status = selectRemoveCompositeStatus(thunkAPi.getState())
    if (status === 'pending') {
      return false;
    }
  }
})

export const removePosition = createAppAsyncThunk('commandBar/removePosition', async (data: { accessToken: string, position: Position }) => {
  const { accessToken, position } = data;
  const user = await getUser(accessToken);
  if (user) {
    if (await deletePosition(accessToken, user.id, position)) {
      return instanceToPlain(position);
    };
  } else {
    return undefined;
  }
}, {
  condition(arg, thunkAPi) {
    const status = selectRemovePositionStatus(thunkAPi.getState())
    if (status === 'pending') {
      return false;
    }
  }
})

const commandBarSlice = createSlice({
  name: 'commandBar',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(removeComposite.pending, (state, action) => {
        state.removeCompositeStatus = 'pending'
      })
      .addCase(removeComposite.fulfilled, (state, action) => {
        state.removeCompositeStatus = action.payload ? 'succeeded' : 'failed'
      })
      .addCase(removeComposite.rejected, (state, action) => {
        state.removeCompositeStatus = 'failed'
      })
      .addCase(removePosition.pending, (state, action) => {
        state.removePositionStatus = 'pending'
      })
      .addCase(removePosition.fulfilled, (state, action) => {
        state.removePositionStatus = action.payload ? 'succeeded' : 'failed'
      })
      .addCase(removePosition.rejected, (state, action) => {
        state.removePositionStatus = 'failed'
      })
  }
})

// Export the auto-generated action creator with the same name

// Export selectors
export const selectRemoveCompositeStatus = (state: RootState) => state.commandBar.removeCompositeStatus;
export const selectRemovePositionStatus = (state: RootState) => state.commandBar.removePositionStatus;

// Export the generated reducer function
export default commandBarSlice.reducer;